import { cn } from '@pcln/horizon'

const hotelTypeVariants = {
  single: {
    gridContainer: [
      'grid-cols-1 gap-3 auto-rows-auto',
      "[grid-template-areas:'radio-option'_'location-input'_'date-picker'_'travelers'_'pkg-deals'_'submit-btn']",
      "sm:grid-cols-2 sm:[grid-template-areas:'radio-option_.'_'location-input_location-input'_'date-picker_travelers'_'pkg-deals_submit-btn']",
      'lg:grid-cols-[1fr_1fr_1fr_max-content]',
      "lg:[grid-template-areas:'radio-option_._._.'_'location-input_date-picker_travelers_submit-btn'_'pkg-deals_pkg-deals_pkg-deals_pkg-deals']"
    ],
    formContainer: ['single']
  },
  singleWithSelectedFilters: {
    gridContainer: [
      'grid-cols-1 gap-3 auto-rows-auto',
      "[grid-template-areas:'radio-option'_'location-input'_'selected-filters'_'date-picker'_'travelers'_'pkg-deals'_'submit-btn']",
      "sm:grid-cols-2 sm:[grid-template-areas:'radio-option_.'_'location-input_location-input'_'selected-filters_selected-filters'_'date-picker_travelers'_'pkg-deals_submit-btn']",
      'lg:grid-cols-[1fr_1fr_1fr_max-content]',
      "lg:[grid-template-areas:'radio-option_._._.'_'location-input_date-picker_travelers_submit-btn'_'selected-filters_selected-filters_selected-filters_selected-filters'_'pkg-deals_pkg-deals_pkg-deals_pkg-deals']"
    ],
    formContainer: ['single']
  },
  singleWithFlights: {
    gridContainer: [
      'grid-cols-1 gap-3 auto-rows-auto',
      "[grid-template-areas:'radio-option'_'flight-input'_'location-input'_'date-picker'_'travelers'_'pkg-deals'_'submit-btn']",
      "sm:grid-cols-2 sm:[grid-template-areas:'radio-option_.'_'flight-input_flight-input'_'location-input_location-input'_'date-picker_travelers'_'pkg-deals_submit-btn']",
      'lg:grid-cols-[minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1fr)_max-content]',
      "lg:[grid-template-areas:'radio-option_._._._.'_'flight-input_location-input_date-picker_travelers_submit-btn'_'pkg-deals_pkg-deals_pkg-deals_pkg-deals_pkg-deals']"
    ],
    formContainer: ['single']
  },
  // Compact variants - maintain 2-column (sm) layout through lg for homepage hero
  singleCompact: {
    gridContainer: [
      'grid-cols-1 gap-3 auto-rows-auto',
      "[grid-template-areas:'radio-option'_'location-input'_'date-picker'_'travelers'_'pkg-deals'_'submit-btn']",
      "sm:grid-cols-2 sm:[grid-template-areas:'radio-option_.'_'location-input_location-input'_'date-picker_travelers'_'pkg-deals_submit-btn']"
    ],
    formContainer: ['single']
  },
  singleWithSelectedFiltersCompact: {
    gridContainer: [
      'grid-cols-1 gap-3 auto-rows-auto',
      "[grid-template-areas:'radio-option'_'location-input'_'selected-filters'_'date-picker'_'travelers'_'pkg-deals'_'submit-btn']",
      "sm:grid-cols-2 sm:[grid-template-areas:'radio-option_.'_'location-input_location-input'_'selected-filters_selected-filters'_'date-picker_travelers'_'pkg-deals_submit-btn']"
    ],
    formContainer: ['single']
  },
  singleWithFlightsCompact: {
    gridContainer: [
      'grid-cols-1 gap-3 auto-rows-auto',
      "[grid-template-areas:'radio-option'_'flight-input'_'location-input'_'date-picker'_'travelers'_'pkg-deals'_'submit-btn']",
      "sm:grid-cols-2 sm:[grid-template-areas:'radio-option_.'_'flight-input_location-input'_'date-picker_travelers'_'pkg-deals_submit-btn']"
    ],
    formContainer: ['single']
  },
  singleSticky: {
    gridContainer: [
      'grid-cols-1 gap-2 auto-rows-auto',
      'md:grid-cols-[1fr_1fr_1fr_max-content] md:gap-3',
      "[grid-template-areas:'location-input_date-picker_travelers_submit-btn'_'pkg-deals_pkg-deals_pkg-deals_pkg-deals']",
      'items-center text-left'
    ],
    formContainer: ['single sticky']
  },
  singleWithSelectedFiltersSticky: {
    gridContainer: [
      'grid-cols-1 gap-2 auto-rows-auto',
      'md:grid-cols-[1fr_1fr_1fr_max-content] md:gap-3',
      "[grid-template-areas:'location-input_date-picker_travelers_submit-btn'_'selected-filters_selected-filters_selected-filters_selected-filters'_'pkg-deals_pkg-deals_pkg-deals_pkg-deals']",
      'items-center text-left'
    ],
    formContainer: ['single sticky']
  },
  singleWithFlightsSticky: {
    gridContainer: [
      'grid-cols-1 gap-2 auto-rows-auto',
      'md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.8fr)_max-content] md:gap-3',
      "[grid-template-areas:'flight-input_location-input_date-picker_travelers_submit-btn'_'pkg-deals_pkg-deals_pkg-deals_pkg-deals_pkg-deals']",
      'items-center text-left'
    ],
    formContainer: ['single sticky']
  },
  multi: {
    gridContainer: 'grid-cols-1',
    formContainer: 'multi'
  }
}

type HotelTypeVariant = keyof typeof hotelTypeVariants
const hotelFormSlots = {
  formContainer: 'search-form-container w-auto',
  gridContainer: 'grid-container grid w-full [grid-area:grid-container]',
  radioGroup:
    'radio-select [grid-area:radio-option] grid grid-cols-max-content',
  locationInput: 'location-input [grid-area:location-input]',
  flightInput: 'flight-input [grid-area:flight-input]',
  datePicker: 'date-picker [grid-area:date-picker] **:translate-y-0',
  travelersInput: 'travelers-selection [grid-area:travelers] **:translate-y-0',
  packageDeals: 'pkg-deals [grid-area:pkg-deals] flex-wrap',
  packageDealsSticky:
    'pkg-deals mb-4 mt-0 [grid-area:pkg-deals] grid grid-cols-[max-content_max-content_max-content] gap-3 flex-wrap',
  submitButton: '[grid-area:submit-btn]',
  selectedFilters: 'selected-filters [grid-area:selected-filters]'
}

const getHotelTypeVariant = (
  addFlights: boolean,
  isStickyInEffect = false,
  addSelectedFilters = false,
  useCompactLayout = false
): HotelTypeVariant => {
  if (isStickyInEffect) {
    if (addFlights) {
      return 'singleWithFlightsSticky'
    }
    if (addSelectedFilters) {
      return 'singleWithSelectedFiltersSticky'
    }
    return 'singleSticky'
  }

  if (useCompactLayout) {
    if (addSelectedFilters && !addFlights) {
      return 'singleWithSelectedFiltersCompact'
    }
    return addFlights ? 'singleWithFlightsCompact' : 'singleCompact'
  }

  if (addSelectedFilters && !addFlights) {
    return 'singleWithSelectedFilters'
  }
  return addFlights ? 'singleWithFlights' : 'single'
}

export const getFormContainerClass = (
  addFlights: boolean,
  isStickyInEffect: boolean,
  addSelectedFilters: boolean,
  useCompactLayout = false
) => {
  const hotelTypeVariant = getHotelTypeVariant(
    addFlights,
    isStickyInEffect,
    addSelectedFilters,
    useCompactLayout
  )
  const styles = hotelTypeVariants[hotelTypeVariant]
  return cn(hotelFormSlots.formContainer, styles.formContainer)
}

export const getGridContainerClass = (
  addFlights: boolean,
  isStickyInEffect: boolean,
  addSelectedFilters: boolean,
  useCompactLayout = false
) => {
  const hotelTypeVariant = getHotelTypeVariant(
    addFlights,
    isStickyInEffect,
    addSelectedFilters,
    useCompactLayout
  )
  const styles = hotelTypeVariants[hotelTypeVariant]
  return cn(hotelFormSlots.gridContainer, styles.gridContainer)
}

export const getInputClass = (
  type:
    | 'radio'
    | 'location'
    | 'flight'
    | 'datePicker'
    | 'travelers'
    | 'packageDeals'
    | 'submit'
    | 'selectedFilters',
  isStickyInEffect = false
) => {
  const inputMap = {
    radio: hotelFormSlots.radioGroup,
    location: hotelFormSlots.locationInput,
    flight: hotelFormSlots.flightInput,
    datePicker: hotelFormSlots.datePicker,
    travelers: hotelFormSlots.travelersInput,
    packageDeals: isStickyInEffect
      ? hotelFormSlots.packageDealsSticky
      : hotelFormSlots.packageDeals,
    submit: hotelFormSlots.submitButton,
    selectedFilters: hotelFormSlots.selectedFilters
  } as const

  return inputMap[type]
}
